import * as React from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { DayPicker } from 'react-day-picker'
import { cn } from '../../lib/utils.js'
import { buttonVariants } from './button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'
import PropTypes from 'prop-types'
function Calendar ({
    className,
    classNames,
    showOutsideDays = true,
    minDate,
    maxDate,
    ...props
}) {
    function handleCalendarChange (value, e) {
        const event = {
            target: {
                value: String(value)
            }
        }
        e(event)
    }

    return (
        (<DayPicker
            showOutsideDays={showOutsideDays}
            className={cn('p-4', className)}
            disabled={date => (minDate && date < new Date(minDate).setHours(0, 0, 0, 0)) || (maxDate && date > new Date(maxDate).setHours(23, 59, 59, 999))}
            classNames={{
                months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
                month: 'space-y-3',
                caption_start: 'is-start',
                caption_between: 'is-between',
                caption_end: 'is-end',
                caption: 'flex justify-center pt-1 relative items-center gap-1',
                caption_label: 'flex h-7 text-sm font-medium justify-center items-center grow [.is-multiple_&]:flex',
                caption_dropdowns: 'flex justify-center grow dropdowns',
                multiple_months: 'is-multiple',
                vhidden: 'hidden [.is-between_&]:flex [.is-end_&]:flex [.is-start.is-end_&]:hidden',
                nav: "flex items-center [&:has([name='previous-month'])]:order-first [&:has([name='next-month'])]:order-last",
                nav_button: cn(
                    buttonVariants({ variant: 'outline' }),
                    'h-6 w-6 bg-transparent p-0 text-primary'
                ),
                nav_button_previous: 'absolute left-1',
                nav_button_next: 'absolute right-1',
                table: 'w-full border-collapse space-y-1',
                head_row: 'flex',
                head_cell: 'text-primary rounded-md w-8 font-normal text-[0.8rem]',
                row: 'flex w-full mt-2',
                cell: cn(
                    'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent',
                    props.mode === 'range'
                        ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
                        : '[&:has([aria-selected])]:rounded-md'
                ),
                day: cn(
                    buttonVariants({ variant: 'ghost' }),
                    'h-8 w-8 p-0 font-normal aria-selected:opacity-100'
                ),
                day_range_start: 'day-range-start',
                day_range_end: 'day-range-end',
                day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
                day_today: 'bg-accent text-accent-foreground',
                day_outside: 'text-muted-foreground opacity-50',
                day_disabled: 'text-muted-foreground opacity-50',
                day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
                day_hidden: 'invisible',
                ...classNames
            }}
            components={{
                IconLeft: ({ ...props }) => <ChevronLeftIcon className="h-4 w-4" />,
                IconRight: ({ ...props }) => <ChevronRightIcon className="h-4 w-4" />,
                Dropdown: ({ ...props }) => (
                    <Select
                        {...props}
                        onValueChange={(value) => {
                            if (props.onChange) {
                                handleCalendarChange(value, props.onChange)
                            }
                        }}
                        value={props.value}
                    >
                        <SelectTrigger className={cn(buttonVariants({ variant: 'ghost' }), 'pl-2 pr-1 py-2 h-7 w-fit font-medium [.is-between_&]:hidden [.is-end_&]:hidden [.is-start.is-end_&]:flex')}>
                            <SelectValue placeholder={props?.caption} >{props?.caption}</SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-[var(--radix-popper-available-height);] overflow-y-auto scrolling-auto min-w-[var(--radix-popper-anchor-width)]">
                            {props.children &&
                React.Children.map(props.children, (child) =>
                    <SelectItem value={(child)?.props?.value} className="min-w-[var(--radix-popper-anchor-width)]">{(child)?.props?.children}</SelectItem>
                )
                            }
                        </SelectContent>
                    </Select>
                )
            }}
            {...props} />)
    )
}
Calendar.displayName = 'Calendar'

Calendar.propTypes = {
    className: PropTypes.string,
    classNames: PropTypes.string,
    showOutsideDays: PropTypes.bool,
    children: PropTypes.any,
    onChange: PropTypes.func,
    caption: PropTypes.any,
    value: PropTypes.string || PropTypes.number,
    mode: PropTypes.any
}

export { Calendar }
